import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { FaChartLine, FaMoneyBillWave } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Calculator = () => {
  const [activeTab, setActiveTab] = useState("sip");
  const [calcMode, setCalcMode] = useState("invest");
  const [amount, setAmount] = useState(5000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const [goal, setGoal] = useState(1000000);
  const [graphData, setGraphData] = useState([]);
  const [activeChart, setActiveChart] = useState("area");

  const calculateSIP = () => {
    const n = years * 12;
    const r = rate / 100 / 12;
    return amount * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  };

  const calculateLumpsum = () => {
    return amount * Math.pow(1 + rate / 100, years);
  };

  const calculateGoalSIP = () => {
    const n = years * 12;
    const r = rate / 100 / 12;
    const sipAmount = goal / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    return isFinite(sipAmount) ? sipAmount : 0;
  };

  const prepareGraphData = () => {
    const data = [];
    
    if (activeTab === "sip") {
      if (calcMode === "invest") {
        for (let year = 1; year <= years; year++) {
          const months = year * 12;
          const r = rate / 100 / 12;
          const invested = amount * months;
          const value = amount * ((Math.pow(1 + r, months) - 1) / r) * (1 + r);
          
          data.push({
            year: `Year ${year}`,
            invested: Math.round(invested),
            value: Math.round(value),
            returns: Math.round(value - invested)
          });
        }
      } else {
        const requiredSIP = calculateGoalSIP();
        for (let year = 1; year <= years; year++) {
          const months = year * 12;
          const r = rate / 100 / 12;
          const invested = requiredSIP * months;
          const value = requiredSIP * ((Math.pow(1 + r, months) - 1) / r) * (1 + r);
          
          data.push({
            year: `Year ${year}`,
            invested: Math.round(invested),
            value: Math.round(value),
            returns: Math.round(value - invested)
          });
        }
      }
    } else {
      for (let year = 1; year <= years; year++) {
        const invested = amount;
        const value = amount * Math.pow(1 + rate / 100, year);
        
        data.push({
          year: `Year ${year}`,
          invested: Math.round(invested),
          value: Math.round(value),
          returns: Math.round(value - invested)
        });
      }
    }
    
    setGraphData(data);
  };

  useEffect(() => {
    prepareGraphData();
    // eslint-disable-next-line 
  }, [activeTab, calcMode, amount, years, rate, goal]);

  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xl">
          <p className="font-bold text-gray-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="flex items-center text-sm" style={{ color: entry.color }}>
              <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
              {entry.name}: ₹{entry.value?.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const investmentData = [
    { name: 'Stocks', value: 35, color: '#3B82F6' },
    { name: 'Mutual Funds', value: 25, color: '#10B981' },
    { name: 'Bonds', value: 20, color: '#8B5CF6' },
    { name: 'Real Estate', value: 15, color: '#F59E0B' },
    { name: 'Others', value: 5, color: '#EF4444' }
  ];

  // Input field handlers with validation
  const handleAmountChange = (e) => {
    const value = Math.max(0, Number(e.target.value.replace(/[^0-9]/g, '')));
    if (activeTab === "sip" && calcMode === "invest") {
      setAmount(Math.min(value, 100000));
    } else {
      setAmount(Math.min(value, 10000000));
    }
  };

  const handleGoalChange = (e) => {
    const value = Math.max(0, Number(e.target.value.replace(/[^0-9]/g, '')));
    setGoal(Math.min(value, 10000000));
  };

  const handleYearsChange = (e) => {
    const value = Math.max(1, Math.min(30, Number(e.target.value)));
    setYears(value);
  };

  const handleRateChange = (e) => {
    const value = Math.max(1, Math.min(30, Number(e.target.value)));
    setRate(value);
  };

  return (
    <section className="py-8 md:py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-8 md:mb-12"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#02ca02] to-[#fe0000] bg-clip-text text-transparent mb-3 md:mb-4">
            Smart Investment Calculator
          </h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">
            Plan your financial future with precision. Calculate returns, set goals, and visualize your wealth growth.
          </p>
        </motion.div>

        {/* Calculator Card */}
        <div className="bg-white rounded-xl md:rounded-3xl shadow-lg md:shadow-2xl overflow-hidden max-w-6xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-col sm:flex-row border-b border-gray-200 w-full">
            <button
              className={`flex-1 py-4 sm:py-6 font-semibold text-base sm:text-lg transition-all duration-300 ${
                activeTab === "sip"
                  ? "bg-gradient-to-r from-[#02ca02] to-[#02a102] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("sip")}
            >
              <div className="flex items-center justify-center">
                <FaChartLine className="mr-2 sm:mr-3 text-sm sm:text-base" />
                <span className="text-sm sm:text-base text-center">
                  Systematic Investment (SIP)
                </span>
              </div>
            </button>

            <button
              className={`flex-1 py-4 sm:py-6 font-semibold text-base sm:text-lg transition-all duration-300 ${
                activeTab === "lumpsum"
                  ? "bg-gradient-to-r from-[#fe0000] to-[#e00000] text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("lumpsum")}
            >
              <div className="flex items-center justify-center">
                <FaMoneyBillWave className="mr-2 sm:mr-3 text-sm sm:text-base" />
                <span className="text-sm sm:text-base text-center">
                  Lumpsum Investment
                </span>
              </div>
            </button>
          </div>

          <div className="p-4 sm:p-6 md:p-8">
            {/* SIP Content */}
            {activeTab === "sip" && (
              <div className="overflow-visible">
                <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-6 md:mb-8">
                  <button
                    className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                      calcMode === "invest"
                        ? "bg-blue-500 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setCalcMode("invest")}
                  >
                    💰 I want to invest
                  </button>
                  <button
                    className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                      calcMode === "goal"
                        ? "bg-green-500 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setCalcMode("goal")}
                  >
                    🎯 I know my goal
                  </button>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
                  {/* Input Section */}
                  <div className="space-y-4 md:space-y-6 min-w-0">
                    {calcMode === "invest" ? (
                      <>
                        <div className="w-full">
                          <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                            Monthly Investment
                          </label>
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="text"
                              value={`₹${amount.toLocaleString()}`}
                              onChange={handleAmountChange}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                              placeholder="Enter amount"
                            />
                            <span className="text-sm text-gray-500 whitespace-nowrap">per month</span>
                          </div>
                          <input
                            type="range"
                            min="500"
                            max="100000"
                            step="500"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            className="w-full h-2 md:h-3 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                            <span>₹500</span>
                            <span>₹1,00,000</span>
                          </div>
                        </div>

                        <div className="w-full">
                          <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                            Investment Period
                          </label>
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={years}
                              onChange={handleYearsChange}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-700"
                              placeholder="Enter years"
                            />
                            <span className="text-sm text-gray-500 whitespace-nowrap">years</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="30"
                            value={years}
                            onChange={(e) => setYears(Number(e.target.value))}
                            className="w-full h-2 md:h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                            <span>1 Year</span>
                            <span>30 Years</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-full">
                          <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                            Target Amount
                          </label>
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="text"
                              value={`₹${goal.toLocaleString()}`}
                              onChange={handleGoalChange}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700"
                              placeholder="Enter target amount"
                            />
                          </div>
                          <input
                            type="range"
                            min="100000"
                            max="10000000"
                            step="10000"
                            value={goal}
                            onChange={(e) => setGoal(Number(e.target.value))}
                            className="w-full h-2 md:h-3 bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                            <span>₹1L</span>
                            <span>₹1Cr</span>
                          </div>
                        </div>

                        <div className="w-full">
                          <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                            Time Horizon
                          </label>
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={years}
                              onChange={handleYearsChange}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-700"
                              placeholder="Enter years"
                            />
                            <span className="text-sm text-gray-500 whitespace-nowrap">years</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="30"
                            value={years}
                            onChange={(e) => setYears(Number(e.target.value))}
                            className="w-full h-2 md:h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                            <span>1 Year</span>
                            <span>30 Years</span>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="w-full">
                      <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                        Expected Return
                      </label>
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={rate}
                          onChange={handleRateChange}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700"
                          placeholder="Enter rate"
                        />
                        <span className="text-sm text-gray-500 whitespace-nowrap">% per annum</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="30"
                        value={rate}
                        onChange={(e) => setRate(Number(e.target.value))}
                        className="w-full h-2 md:h-3 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                        <span>1%</span>
                        <span>30%</span>
                      </div>
                    </div>
                  </div>

                  {/* Results Section */}
                  <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 mt-4 md:mt-0 min-w-0">
                    <h3 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6 text-gray-800">
                      {calcMode === "invest" ? "SIP Projection" : "Goal Achievement Plan"}
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-3 md:gap-4 lg:gap-6">
                      {calcMode === "invest" ? (
                        <>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-blue-600 truncate">₹{amount.toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Monthly Investment</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-green-600 truncate">₹{(amount * years * 12).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Total Invested</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-purple-600 truncate">₹{Math.round(calculateSIP()).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Future Value</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-orange-600 truncate">₹{Math.round(calculateSIP() - (amount * years * 12)).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Wealth Gain</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-green-600 truncate">₹{goal.toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Target Amount</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-blue-600 truncate">₹{Math.round(calculateGoalSIP()).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Monthly SIP Needed</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-purple-600 truncate">₹{Math.round(calculateGoalSIP() * years * 12).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Total Investment</div>
                          </div>
                          <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                            <div className="text-lg md:text-xl lg:text-2xl font-bold text-orange-600 truncate">₹{Math.round(goal - (calculateGoalSIP() * years * 12)).toLocaleString()}</div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">Wealth Gain</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Lumpsum Content */}
            {activeTab === "lumpsum" && (
              <div className="grid lg:grid-cols-2 gap-6 md:gap-8 overflow-visible">
                <div className="space-y-4 md:space-y-6 min-w-0">
                  <div className="w-full">
                    <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                      Investment Amount
                    </label>
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="text"
                        value={`₹${amount.toLocaleString()}`}
                        onChange={handleAmountChange}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-700"
                        placeholder="Enter amount"
                      />
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="10000000"
                      step="1000"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="w-full h-2 md:h-3 bg-gradient-to-r from-red-400 to-red-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                      <span>₹1,000</span>
                      <span>₹1Cr</span>
                    </div>
                  </div>

                  <div className="w-full">
                    <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                      Investment Period
                    </label>
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={years}
                        onChange={handleYearsChange}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-700"
                        placeholder="Enter years"
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">years</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={years}
                      onChange={(e) => setYears(Number(e.target.value))}
                      className="w-full h-2 md:h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                      <span>1 Year</span>
                      <span>30 Years</span>
                    </div>
                  </div>

                  <div className="w-full">
                    <label className="block text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-700">
                      Expected Return
                    </label>
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={rate}
                        onChange={handleRateChange}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700"
                        placeholder="Enter rate"
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">% per annum</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value))}
                      className="w-full h-2 md:h-3 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                      <span>1%</span>
                      <span>30%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 mt-4 md:mt-0 min-w-0">
                  <h3 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6 text-gray-800">Lumpsum Projection</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4 lg:gap-6">
                    <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                      <div className="text-lg md:text-xl lg:text-2xl font-bold text-red-600 truncate">₹{amount.toLocaleString()}</div>
                      <div className="text-xs md:text-sm text-gray-600 truncate">Initial Investment</div>
                    </div>
                    <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                      <div className="text-lg md:text-xl lg:text-2xl font-bold text-gray-600 truncate">{years} Years</div>
                      <div className="text-xs md:text-sm text-gray-600 truncate">Investment Period</div>
                    </div>
                    <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                      <div className="text-lg md:text-xl lg:text-2xl font-bold text-green-600 truncate">₹{Math.round(calculateLumpsum()).toLocaleString()}</div>
                      <div className="text-xs md:text-sm text-gray-600 truncate">Future Value</div>
                    </div>
                    <div className="text-center p-2 md:p-3 lg:p-4 bg-white rounded-lg md:rounded-xl shadow-sm min-w-0">
                      <div className="text-lg md:text-xl lg:text-2xl font-bold text-purple-600 truncate">₹{Math.round(calculateLumpsum() - amount).toLocaleString()}</div>
                      <div className="text-xs md:text-sm text-gray-600 truncate">Wealth Gain</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Charts Section */}
        {graphData.length > 0 && (
          <motion.div 
            className="mt-8 md:mt-16 bg-white rounded-xl md:rounded-3xl shadow-lg md:shadow-2xl p-4 md:p-6 lg:p-8 overflow-visible" 
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col lg:flex-row justify-between items-center mb-6 md:mb-8">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#02ca02] to-[#fe0000] bg-clip-text text-transparent text-center lg:text-left mb-4 lg:mb-0">
                Investment Growth Visualization
              </h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {["area", "bar", "line", "pie"].map((chartType) => (
                  <button
                    key={chartType}
                    onClick={() => setActiveChart(chartType)}
                    className={`px-3 py-1 md:px-4 md:py-2 rounded-lg font-semibold transition-all text-xs md:text-sm ${
                      activeChart === chartType
                        ? "bg-gradient-to-r from-[#02ca02] to-[#fe0000] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {chartType.charAt(0).toUpperCase() + chartType.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-64 sm:h-80 md:h-96 w-full overflow-visible">
              {/* Charts remain the same */}
              {activeChart === "area" && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={graphData}>
                    <defs>
                      <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="investedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="year" stroke="#666" fontSize={12} />
                    <YAxis tickFormatter={(value) => `₹${(value/100000).toFixed(0)}L`} stroke="#666" fontSize={12} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                    <Area type="monotone" dataKey="invested" stroke="#3b82f6" fillOpacity={1} fill="url(#investedGradient)" name="Amount Invested" />
                    <Area type="monotone" dataKey="value" stroke="#10b981" fillOpacity={1} fill="url(#valueGradient)" name="Future Value" />
                  </AreaChart>
                </ResponsiveContainer>
              )}

              {activeChart === "bar" && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graphData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="year" stroke="#666" fontSize={12} />
                    <YAxis tickFormatter={(value) => `₹${(value/100000).toFixed(0)}L`} stroke="#666" fontSize={12} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                    <Bar dataKey="invested" fill="#3b82f6" radius={[2, 2, 0, 0]} name="Amount Invested" />
                    <Bar dataKey="value" fill="#10b981" radius={[2, 2, 0, 0]} name="Future Value" />
                    <Bar dataKey="returns" fill="#8b5cf6" radius={[2, 2, 0, 0]} name="Returns" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {activeChart === "line" && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={graphData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="year" stroke="#666" fontSize={12} />
                    <YAxis tickFormatter={(value) => `₹${(value/100000).toFixed(0)}L`} stroke="#666" fontSize={12} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                    <Line type="monotone" dataKey="invested" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', strokeWidth: 1, r: 3 }} name="Amount Invested" />
                    <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', strokeWidth: 1, r: 3 }} name="Future Value" />
                    <Line type="monotone" dataKey="returns" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', strokeWidth: 1, r: 3 }} name="Returns" />
                  </LineChart>
                </ResponsiveContainer>
              )}

              {activeChart === "pie" && (
                <div className="flex flex-col lg:flex-row items-center justify-center h-full w-full overflow-visible">
                  <div className="w-full h-44 sm:h-56 md:h-64 lg:h-72 lg:w-1/2 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={investmentData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {investmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="lg:ml-4 mt-4 lg:mt-0 w-full lg:w-auto">
                    <h4 className="font-bold mb-2 text-sm md:text-base text-center lg:text-left">Portfolio Allocation</h4>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col">
                      {investmentData.map((item, index) => (
                        <div key={index} className="flex items-center mb-1 justify-center lg:justify-start">
                          <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: item.color }}></div>
                          <span className="text-xs md:text-sm">{item.name}: {item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
          border: 2px solid white;
          transition: all 0.3s ease;
        }
        
        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
        }
        
        .slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #6366f1);
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
          border: 2px solid white;
        }

        @media (min-width: 768px) {
          .slider::-webkit-slider-thumb {
            width: 24px;
            height: 24px;
            border: 3px solid white;
          }
          
          .slider::-moz-range-thumb {
            width: 24px;
            height: 24px;
            border: 3px solid white;
          }
        }
      `}</style>
    </section>
  );
};

export default Calculator;